import { useEffect, useMemo } from "react";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { buildSeo } from "@/utils/seo";

export interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  path?: string;
  image?: string;
  robots?: string;
  jsonLd?: Record<string, unknown>;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Emits every SEO tag a page needs. Only a plain-language title / description /
 * keywords / image are required — everything else (length-safe description,
 * derived keywords, canonical URL, Open Graph + Twitter cards, share image) is
 * auto-generated by `buildSeo`, so pages stay consistent and audit-friendly.
 */
export default function Seo({
  title,
  description,
  keywords,
  path = "/",
  image,
  robots = "index, follow",
  jsonLd,
}: SeoProps) {
  const { settings } = useSiteSettings();
  const siteName = settings.siteName;

  // Prefer the page's own share image; otherwise fall back to the admin-managed
  // social share image (Website settings → Branding).
  const shareImage = image || settings.ogImageUrl;

  const seo = useMemo(
    () => buildSeo({ title, description, keywords, path, image: shareImage }),
    [title, description, keywords, path, shareImage],
  );

  const fullTitle = title ? `${title} | ${siteName}` : `${siteName} — Local Business Directory`;

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "keywords", seo.keywords);
    upsertMeta("name", "robots", robots);

    upsertMeta("property", "og:type", seo.ogType);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", seo.canonical);
    upsertMeta("property", "og:site_name", siteName);
    upsertMeta("property", "og:image", seo.image);
    upsertMeta("property", "og:image:alt", fullTitle);
    upsertMeta("property", "og:locale", "en_IN");

    upsertMeta("name", "twitter:card", seo.twitterCard);
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", seo.image);
    upsertMeta("name", "twitter:image:alt", fullTitle);

    upsertLink("canonical", seo.canonical);

    const id = "page-jsonld";
    document.getElementById(id)?.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [fullTitle, seo, siteName, robots, jsonLd]);

  return null;
}